import React from 'react';
import { FaGlobe, FaBars, FaTags, FaUtensils, FaLayerGroup, FaTimes } from 'react-icons/fa';
import './DiscountScopeDisplay.scss';

interface DiscountItem {
  item_id: number;
  item_name: string;
}

interface DiscountCategory {
  category_id: number;
  category_name: string;
}

interface MenuDetail {
  menu_id: number;
  menu_name: string;
}

interface DiscountScopeDisplayProps {
  scopeType: string;
  itemsDetail?: DiscountItem[];
  categoriesDetail?: DiscountCategory[];
  menuDetail?: MenuDetail | null;
  isglobal?: boolean;
  onRemoveItem?: (itemId: number) => void;
  onRemoveCategory?: (categoryId: number) => void;
  itemsToRemove?: number[];
  categoriesToRemove?: number[];
  itemsToAdd?: number[];
  categoriesToAdd?: number[];
  availableItems?: Array<{id: number; name: string}>;
  availableCategories?: Array<{id: number; name: string}>;
}

const DiscountScopeDisplay: React.FC<DiscountScopeDisplayProps> = ({
  scopeType,
  itemsDetail = [],
  categoriesDetail = [],
  menuDetail,
  isglobal,
  onRemoveItem,
  onRemoveCategory,
  itemsToRemove = [],
  categoriesToRemove = [],
  itemsToAdd = [],
  categoriesToAdd = [],
  availableItems = [],
  availableCategories = []
}) => {
  
  const getScopeIcon = () => {
    if (isglobal || scopeType === 'global') return <FaGlobe />;
    if (scopeType === 'menu') return <FaBars />;
    if (scopeType === 'category' || scopeType === 'multi_category') return <FaTags />;
    if (scopeType === 'item' || scopeType === 'multi_item') return <FaUtensils />;
    if (scopeType.startsWith('multi')) return <FaLayerGroup />;
    return <FaGlobe />;
  };

  const getScopeTitle = () => {
    if (isglobal || scopeType === 'global') return 'Global';
    if (scopeType === 'menu') return 'Menú';
    if (scopeType === 'category') return 'Categoría';
    if (scopeType === 'multi_category') return 'Múltiples Categorías';
    if (scopeType === 'item') return 'Ítem';
    if (scopeType === 'multi_item') return 'Múltiples Ítems';
    return 'Alcance desconocido';
  };

  // Get items being added from availableItems
  const itemsBeingAdded = availableItems.filter(item => itemsToAdd.includes(item.id));
  
  // Get categories being added from availableCategories
  const categoriesBeingAdded = availableCategories.filter(cat => categoriesToAdd.includes(cat.id));

  return (
    <div className="discount-scope-display">
      <div className="scope-header">
        <div className="scope-icon">{getScopeIcon()}</div>
        <div className="scope-title">{getScopeTitle()}</div>
      </div>

      <div className="scope-content">
        {(isglobal || scopeType === 'global') && (
          <div className="scope-description">
            Este descuento se aplica a <strong>todos los productos</strong> del local
          </div>
        )}

        {scopeType === 'menu' && menuDetail && (
          <div className="scope-description">
            Aplica a todos los ítems del menú: <strong>{menuDetail.menu_name}</strong>
          </div>
        )}

        {(scopeType === 'item' || scopeType === 'multi_item') && (itemsDetail.length > 0 || itemsBeingAdded.length > 0) && (
          <div className="scope-items">
            <div className="items-label">
              {scopeType === 'item' ? 'Ítem:' : 'Ítems aplicables:'} ({itemsDetail.length + itemsBeingAdded.length})
            </div>
            <div className="items-badges">
              {itemsDetail.map(item => (
                <span 
                  key={item.item_id} 
                  className={`item-badge ${itemsToRemove.includes(item.item_id) ? 'marked-for-removal' : ''}`}
                >
                  <FaUtensils className="badge-icon" />
                  {item.item_name}
                  {onRemoveItem && scopeType === 'multi_item' && (
                    <button
                      type="button"
                      className="remove-badge-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveItem(item.item_id);
                      }}
                      title="Remover ítem"
                    >
                      <FaTimes />
                    </button>
                  )}
                </span>
              ))}
              {/* Show items being added as blue badges */}
              {itemsBeingAdded.map(item => (
                <span 
                  key={`new-${item.id}`} 
                  className="item-badge new-item"
                >
                  <FaUtensils className="badge-icon" />
                  {item.name}
                  <span className="new-badge-indicator">Nuevo</span>
                  {onRemoveItem && (
                    <button
                      type="button"
                      className="remove-badge-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveItem(item.id);
                      }}
                      title="Cancelar"
                    >
                      <FaTimes />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {(scopeType === 'category' || scopeType === 'multi_category') && (categoriesDetail.length > 0 || categoriesBeingAdded.length > 0) && (
          <div className="scope-categories">
            <div className="categories-label">
              {scopeType === 'category' ? 'Categoría:' : 'Categorías aplicables:'} ({categoriesDetail.length + categoriesBeingAdded.length})
            </div>
            <div className="categories-badges">
              {categoriesDetail.map(category => (
                <span 
                  key={category.category_id} 
                  className={`category-badge ${categoriesToRemove.includes(category.category_id) ? 'marked-for-removal' : ''}`}
                >
                  <FaTags className="badge-icon" />
                  {category.category_name}
                  {onRemoveCategory && scopeType === 'multi_category' && (
                    <button
                      type="button"
                      className="remove-badge-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveCategory(category.category_id);
                      }}
                      title="Remover categoría"
                    >
                      <FaTimes />
                    </button>
                  )}
                </span>
              ))}
              {/* Show categories being added as blue badges */}
              {categoriesBeingAdded.map(category => (
                <span 
                  key={`new-${category.id}`} 
                  className="category-badge new-category"
                >
                  <FaTags className="badge-icon" />
                  {category.name}
                  <span className="new-badge-indicator">Nuevo</span>
                  {onRemoveCategory && (
                    <button
                      type="button"
                      className="remove-badge-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveCategory(category.id);
                      }}
                      title="Cancelar"
                    >
                      <FaTimes />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountScopeDisplay;
